import { Float32BufferAttribute, type BufferGeometry } from 'three';
import type { Vec3 } from '../../geo/types';
import { createMeshBuilder, type MeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import type { SailState } from './shipTemplates';
import { deckAt, highestDeckAt, topAt, type FormedTemplate } from './shipForm';
import { clothMembrane, sparSolid } from './shipSolids';
import { RIG_SPAN, YARD, YARD_PAINTER_WIDTH_SCALE, type RigStyles } from './rigParts';
import { formedMasts, lowerRadiusAt, topmastRadiusAt, type FormedMast } from './shipMastPlan';
import { joinGeometries } from './geometryJoin';
import { normalOffsets } from './painterNormals';
import { belliedNormal, type DrawnCourse } from './courseBelly';
import { sailclothStyle } from './shipPatterns';
import { spreadOn } from './sailPlan';

/*
 * The running rig of a formed ship (docs/plans/ships.md 3): on every square mast a course yard
 * slung on the fore side under the top, its course set and bellied (R3, R8) or furled along it;
 * on a topmast a topsail yard with its sail furled (woodcut); on a lateen mast a yard crossing
 * the mast on its lee side, low end forward, its three-cornered sail set below it or furled
 * along it (R3, woodcut, Wyngaerde). Square yards swing about their masts by the brace; a lateen
 * yard is a fore-and-aft spar and swings its after end to the lee by a third of it.
 *
 * The painter draws the courses of his ships under sail face on and shows no topsail yard on
 * them (R1, R8, R20, R25, R26; docs/plans/ships.md 6). In the painter view a set course and its
 * yard swing on about their mast to PAINTER_BRACE, lee arm aft, the course let down to the rail
 * as he draws it (R8, R26), and a topsail yard squares; a furled course yard, which he leaves
 * out, is drawn at the span the ship is placed by (shipFootprint.ts), so every ship's rig in the
 * painter view stays inside its plan.
 */

/** A lateen yard swings this share of the ship's brace, its after end to the lee. */
export const LATEEN_SWING = 1 / 3;

/** Course yards as a share of the ship's length: the painted courses of ships under sail span
    0.45-0.58 of the hull on the main mast (R1, R8, R20, R26) and 0.40-0.42 on the fore mast (R8,
    R16, R26), drawn face on; the woodcut's run 0.55-0.63. The main mast is the tallest square. */
export const COURSE_YARD = { main: 0.5, fore: 0.42 } as const;
/** How far below its top a course yard hangs, as a share of the height from the top down to the
    deck beneath: hoisted for a set course (R20 0.09, R8 0.19, R26 0.26, R3's fore 0.32), lowered
    with the course furled (R3's main 0.31; gridded scan 280,4440-700,4810). */
export const COURSE_HOIST = { set: 0.2, furled: 0.3 } as const;
/** A topsail yard's span as a share of the course yard beneath it (woodcut). */
export const TOPSAIL_SPAN = 0.55;
/** How far a set course and its yard swing about the mast in the painter view, lee arm aft. */
export const PAINTER_BRACE = (75 * Math.PI) / 180;

/* A set course drops half the height from the deck beneath to the masthead: the painted courses
   of ships under sail are 0.38-0.49 of the masthead's height above the water deep (R1, R8, R20,
   R26), their feet at the rail. */
const COURSE = { drop: 0.5, clear: 0.4, head: 0.95, clearance: 0.05 } as const;
/** The cells of a set course or lateen sail: 8 across and 6 down, each double-sided. */
export const SAIL_CLOTH = { columns: 8, rows: 6 } as const;
const CLOTH = { ...SAIL_CLOTH, bulge: 0.12, fullest: 0.7 } as const;
const TOPSAIL = { at: 0.55 } as const;
const FURL = {
  course: 0.25,
  topsail: 0.18,
  lateen: 0.12,
  sides: 6,
  length: 0.9,
  tuck: 0.7,
  into: 0.05,
} as const;
/* A lateen yard hangs clear of its mast by its furl and a gap: no further, so the shrouds on its
   side pass above it where they cross its plane. */
const LATEEN_SAIL = { clewAft: 0.35, clewLift: 0.8, bulge: 0.06, gap: 0.03 } as const;
const SWEEP_STEP = 0.01;

/** A square yard on the fore side of its mast, before it is braced about the mast's axis. */
interface SquareYard {
  readonly pivot: number;
  readonly x: number;
  readonly y: number;
  readonly half: number;
  readonly brace: number;
  /** How far the bundle of its furled sail stands forward of the yard's axis. */
  readonly furlX: number;
}

/* Yard, sail and furl swing together about the mast's axis; a positive brace takes the +Z
   yardarm aft, as a rotation about +Y does. */
function swung(pivot: number, angle: number, p: Vec3): Vec3 {
  const [cos, sin] = [Math.cos(angle), Math.sin(angle)];
  const x = p[0] - pivot;
  return [pivot + x * cos + p[2] * sin, p[1], -x * sin + p[2] * cos];
}

function squareYard(
  mast: FormedMast,
  y: number,
  radius: number,
  half: number,
  brace: number,
  furl: number
): SquareYard {
  const x = mast.x - (radius + YARD.radius / 2);
  return { pivot: mast.x, x, y, half, brace, furlX: mast.x - radius - furl + FURL.into };
}

function spar(
  builder: MeshBuilder,
  style: VertexStyle,
  from: Vec3,
  to: Vec3,
  radius: number,
  painter: number
): void {
  sparSolid(builder, style, {
    from,
    to,
    radiusFrom: radius,
    radiusTo: radius,
    sides: YARD.sides,
    painterWidthScale: painter,
  });
}

function buildYard(builder: MeshBuilder, style: VertexStyle, yard: SquareYard): void {
  const end = (z: number): Vec3 => swung(yard.pivot, yard.brace, [yard.x, yard.y, z]);
  spar(builder, style, end(-yard.half), end(yard.half), YARD.radius, YARD_PAINTER_WIDTH_SCALE);
}

function buildFurl(
  builder: MeshBuilder,
  style: VertexStyle,
  yard: SquareYard,
  radius: number
): void {
  const y = yard.y - (YARD.radius + radius * FURL.tuck);
  const end = (z: number): Vec3 => swung(yard.pivot, yard.brace, [yard.furlX, y, z]);
  const half = FURL.length * yard.half;
  spar(builder, style, end(-half), end(half), radius, 1);
}

/* The course hangs just forward of its yard and fills forward, braced with it: fullest 0.7 of the
   way down and its foot still full, as the painted courses' curved feet show (R8, R20, R26). */
function buildCourse(
  builder: MeshBuilder,
  style: VertexStyle,
  yard: SquareYard,
  drop: number
): void {
  const head = COURSE.head * 2 * yard.half;
  const grid = Array.from({ length: CLOTH.rows + 1 }, (_, r) =>
    Array.from({ length: CLOTH.columns + 1 }, (_, c): Vec3 => {
      const [u, v] = [c / CLOTH.columns, r / CLOTH.rows];
      const fill = Math.sin(Math.PI * u) * Math.sin((Math.PI * v) / (2 * CLOTH.fullest));
      const x = yard.x - COURSE.clearance - CLOTH.bulge * drop * fill;
      return swung(yard.pivot, yard.brace, [x, yard.y - v * drop, (u - 0.5) * head]);
    })
  );
  clothMembrane(builder, style, grid);
}

/* The course's foot keeps clear of every rail and castle its yard can swing over. */
function courseDrop(
  template: FormedTemplate,
  mast: FormedMast,
  yardY: number,
  half: number
): number {
  const s = mast.config.position;
  const reach = half / template.length;
  let highest = -Infinity;
  for (let at = Math.max(0, s - reach); at <= Math.min(1, s + reach); at += SWEEP_STEP) {
    highest = Math.max(highest, topAt(template, at));
  }
  const painted = COURSE.drop * (mast.head - deckAt(template, s));
  return Math.min(painted, yardY - highest - COURSE.clear);
}

/** A piece of the running rig as the free view lays it out, how the painter view moves each of
    its points (null: as they lie), and for a set course, the course that view draws. */
interface RigPiece {
  build(builder: MeshBuilder): void;
  readonly painter: ((p: Vec3) => Vec3) | null;
  readonly belly?: DrawnCourse;
}

function courseYardSpan(template: FormedTemplate, mast: FormedMast): number {
  const square = formedMasts(template).filter((m) => (m.config.rig ?? 'square') === 'square');
  const main = square.reduce((a, b) => (b.head > a.head ? b : a));
  return (mast.config === main.config ? COURSE_YARD.main : COURSE_YARD.fore) * template.length;
}

/* A square yard braced `from` turned on about its mast to `to`, all it carries with it. */
function swingTo(pivot: number, from: number, to: number): (p: Vec3) => Vec3 {
  return (p) => swung(pivot, to - from, p);
}

/* A square yard braced `brace` drawn `factor` times as long, all it carries with it. */
function spanned(pivot: number, brace: number, factor: number): (p: Vec3) => Vec3 {
  return (p) => {
    const square = swung(pivot, -brace, p);
    return swung(pivot, brace, [square[0], square[1], square[2] * factor]);
  };
}

/* The painter draws a set course face on and down to the rail (R8, R26): yard and cloth swing on
   about the mast to PAINTER_BRACE, lee arm aft, the cloth let down first in its own plane until
   its foot clears the hull beneath the mast, as the free view's clears every castle it swings
   over. */
function setCoursePieces(
  template: FormedTemplate,
  mast: FormedMast,
  yard: SquareYard,
  styles: RigStyles
): readonly RigPiece[] {
  const drop = courseDrop(template, mast, yard.y, yard.half);
  const letDown = (yard.y - topAt(template, mast.config.position) - COURSE.clear) / drop;
  const faceOn = swingTo(yard.pivot, yard.brace, (yard.brace < 0 ? -1 : 1) * PAINTER_BRACE);
  const cloth = (p: Vec3): Vec3 => faceOn([p[0], yard.y - (yard.y - p[1]) * letDown, p[2]]);
  const headEnd = (side: number): Vec3 =>
    cloth(
      swung(yard.pivot, yard.brace, [
        yard.x - COURSE.clearance,
        yard.y,
        side * COURSE.head * yard.half,
      ])
    );
  return [
    {
      build: (builder) => {
        buildYard(builder, styles.spar, yard);
      },
      painter: faceOn,
    },
    {
      build: (builder) => {
        buildCourse(builder, sailclothStyle(styles.sail, drop), yard, drop);
      },
      painter: cloth,
      belly: { head: [headEnd(-1), headEnd(1)], drop: drop * letDown },
    },
  ];
}

function squareMastPieces(
  template: FormedTemplate,
  mast: FormedMast,
  sailState: SailState,
  brace: number,
  styles: RigStyles
): readonly RigPiece[] {
  const set = spreadOn(template, mast, sailState);
  const deck = highestDeckAt(template, mast.config.position);
  const hoist = set ? COURSE_HOIST.set : COURSE_HOIST.furled;
  const y = mast.topLevel - hoist * (mast.topLevel - deck);
  const half = courseYardSpan(template, mast) / 2;
  const course = squareYard(mast, y, lowerRadiusAt(mast, y), half, brace, FURL.course);
  const furled: RigPiece = {
    build: (builder) => {
      buildYard(builder, styles.spar, course);
      buildFurl(builder, styles.sail, course, FURL.course);
    },
    painter: spanned(mast.x, brace, (RIG_SPAN * template.beam) / 2 / half),
  };
  const coursePieces = set ? setCoursePieces(template, mast, course, styles) : [furled];
  if (!mast.config.topmast) return coursePieces;
  const high = mast.topLevel + TOPSAIL.at * (mast.head - mast.topLevel);
  const radius = topmastRadiusAt(mast, high);
  const topsail = squareYard(mast, high, radius, TOPSAIL_SPAN * half, brace, FURL.topsail);
  const topsailPiece: RigPiece = {
    build: (builder) => {
      buildYard(builder, styles.spar, topsail);
      buildFurl(builder, styles.sail, topsail, FURL.topsail);
    },
    painter: set ? swingTo(mast.x, brace, 0) : null,
  };
  return [...coursePieces, topsailPiece];
}

/** A lateen yard in the mast's frame: points along it, on the mast's lee side, swung to the lee. */
interface LateenYard {
  readonly at: (t: number, drop?: number) => Vec3;
  readonly side: number;
  readonly fore: number;
  readonly aft: number;
  readonly place: (p: Vec3) => Vec3;
}

function lateenYard(template: FormedTemplate, mast: FormedMast, brace: number): LateenYard {
  const spec = mast.config.lateen;
  if (!spec) throw new Error(`${template.type}: a lateen mast needs its yard`);
  const deck = highestDeckAt(template, mast.config.position);
  const sling = deck + spec.sling * (mast.head - deck);
  const side = brace < 0 ? -1 : 1;
  const offset = side * (lowerRadiusAt(mast, sling) + FURL.lateen + LATEEN_SAIL.gap);
  const [cos, sin] = [Math.cos(spec.angle), Math.sin(spec.angle)];
  return {
    at: (t, drop = 0) => [mast.x + t * cos, sling + t * sin - drop, offset],
    side,
    fore: -spec.forward * spec.length,
    aft: (1 - spec.forward) * spec.length,
    place: (p) => swung(mast.x, -brace * LATEEN_SWING, p),
  };
}

/* Set, a three-cornered sail from the yard down to a clew abaft the mast, just over the rail
   beneath it; its luff runs along the yard and it fills away from the mast. */
function buildLateenSail(
  builder: MeshBuilder,
  template: FormedTemplate,
  style: VertexStyle,
  yard: LateenYard
): void {
  const [tack, peak] = [yard.at(yard.fore), yard.at(yard.aft)];
  const clewX = yard.at(0)[0] + LATEEN_SAIL.clewAft * (peak[0] - yard.at(0)[0]);
  const clewY = topAt(template, clewX / template.length + 0.5) + LATEEN_SAIL.clewLift;
  const clew: Vec3 = [clewX, clewY, tack[2]];
  const depth = Math.hypot(clewX - (tack[0] + peak[0]) / 2, clewY - (tack[1] + peak[1]) / 2);
  const grid = Array.from({ length: CLOTH.rows + 1 }, (_, r) =>
    Array.from({ length: CLOTH.columns + 1 }, (_, c): Vec3 => {
      const [u, v] = [c / CLOTH.columns, r / CLOTH.rows];
      const luff: Vec3 = [
        tack[0] + (peak[0] - tack[0]) * u,
        tack[1] + (peak[1] - tack[1]) * u,
        tack[2],
      ];
      const fill = LATEEN_SAIL.bulge * depth * Math.sin(Math.PI * u) * Math.sin(Math.PI * v);
      const p: Vec3 = [
        luff[0] + (clew[0] - luff[0]) * v,
        luff[1] + (clew[1] - luff[1]) * v,
        tack[2] + yard.side * fill,
      ];
      return yard.place(p);
    })
  );
  clothMembrane(builder, style, grid);
}

function buildLateenMast(
  builder: MeshBuilder,
  template: FormedTemplate,
  mast: FormedMast,
  sailState: SailState,
  brace: number,
  styles: RigStyles
): void {
  const yard = lateenYard(template, mast, brace);
  spar(
    builder,
    styles.spar,
    yard.place(yard.at(yard.fore)),
    yard.place(yard.at(yard.aft)),
    YARD.radius,
    YARD_PAINTER_WIDTH_SCALE
  );
  if (spreadOn(template, mast, sailState)) {
    buildLateenSail(builder, template, sailclothStyle(styles.sail, 0), yard);
    return;
  }
  const drop = YARD.radius + FURL.lateen * FURL.tuck;
  const [from, to] = [yard.fore * FURL.length, yard.aft * FURL.length];
  spar(
    builder,
    styles.sail,
    yard.place(yard.at(from, drop)),
    yard.place(yard.at(to, drop)),
    FURL.lateen,
    1
  );
}

function runningPieces(
  template: FormedTemplate,
  sailState: SailState,
  brace: number,
  styles: RigStyles
): readonly RigPiece[] {
  return formedMasts(template).flatMap((mast): readonly RigPiece[] =>
    mast.config.rig === 'lateen'
      ? [
          {
            build: (builder) => {
              buildLateenMast(builder, template, mast, sailState, brace, styles);
            },
            painter: null,
          },
        ]
      : squareMastPieces(template, mast, sailState, brace, styles)
  );
}

/** The vertices a builder makes room for, for each piece: a course has 384 (8 x 6 cells of two
    quads of four), a lateen sail with its yard 404, a yard with its furl 72. */
export const PIECE_VERTEX_ROOM = 512;

/* A piece built on its own, each vertex offset to where the painter view draws it and its normal
   turned to the surface drawn there, so a course swung face on is shaded face on, with the belly
   the painter gives it (courseBelly.ts). */
function drawnPiece(piece: RigPiece): BufferGeometry {
  const builder = createMeshBuilder(PIECE_VERTEX_ROOM);
  piece.build(builder);
  const geometry = builder.build();
  const painter = piece.painter;
  if (!painter) return geometry;
  const position = geometry.getAttribute('position');
  const current = geometry.getAttribute('aAxisOffset');
  const widened = (i: number): Vec3 => [
    position.getX(i) + (current?.getX(i) ?? 0),
    position.getY(i) + (current?.getY(i) ?? 0),
    position.getZ(i) + (current?.getZ(i) ?? 0),
  ];
  const offsets = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    const drawn = painter(widened(i));
    offsets.set(
      [drawn[0] - position.getX(i), drawn[1] - position.getY(i), drawn[2] - position.getZ(i)],
      i * 3
    );
  }
  geometry.setAttribute('aAxisOffset', new Float32BufferAttribute(offsets, 3));
  const belly = piece.belly;
  const turn = belly && ((at: Vec3, normal: Vec3): Vec3 => belliedNormal(belly, at, normal));
  geometry.setAttribute('aNormalOffset', normalOffsets(geometry, widened, painter, turn));
  return geometry;
}

/**
 * Yards and sails of a formed ship (bow at -X), as the free view lays them out. `brace` swings
 * square yards about their masts, their +Z arms aft when it is positive, and lateen yards a third
 * as far, after ends to +Z.
 */
export function buildRunningRig(
  builder: MeshBuilder,
  template: FormedTemplate,
  sailState: SailState,
  brace: number,
  styles: RigStyles
): void {
  runningPieces(template, sailState, brace, styles).forEach((piece) => {
    piece.build(builder);
  });
}

/**
 * The running rig as buildRunningRig lays it out, vertex for vertex, each vertex offset to where
 * the painter view draws it: a set course and its yard swung on to PAINTER_BRACE, the course let
 * down to the rail, a topsail yard squared, a furled course yard at the span the ship is placed
 * by.
 */
export function runningRigGeometry(
  template: FormedTemplate,
  sailState: SailState,
  brace: number,
  styles: RigStyles
): BufferGeometry {
  return joinGeometries(runningRigPieces(template, sailState, brace, styles));
}

/** The pieces runningRigGeometry joins, mast by mast: a course with its yard, a topsail yard with
    its furl, a lateen yard with its sail. */
export function runningRigPieces(
  template: FormedTemplate,
  sailState: SailState,
  brace: number,
  styles: RigStyles
): readonly BufferGeometry[] {
  return runningPieces(template, sailState, brace, styles).map(drawnPiece);
}
